// skipcq: JS-W1028, JS-0128
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Grid,
} from '@mui/material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useUserAddedLocations from '../../hooks/useUserAddedLocations';

/**
 * Formats a date to a readable string
 */
function formatDate(timestamp) {
  if (!timestamp) return 'Unknown Date';
  try {
    return new Date(timestamp).toLocaleString();
  } catch {
    return 'Invalid Date';
  }
}

/**
 * Formats an address from location data
 */
function formatAddress(location) {
  const parts = [
    location.address1,
    location.city,
    location.state,
    location.zip,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'No address provided';
}

/**
 * LocationCard component to display a single user-added location
 */
function LocationCard({ location }) {
  return (
    <Card sx={{ width: '100%', maxWidth: 600, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" component="div" gutterBottom>
          {location.name || 'Unnamed Location'}
        </Typography>
        <Typography variant="body2" gutterBottom sx={{
          color: "text.secondary"
        }}>
          Type: {location.type || 'Unknown'}
        </Typography>
        <Typography variant="body2" gutterBottom sx={{
          color: "text.secondary"
        }}>
          Address: {formatAddress(location)}
        </Typography>
        {location.parentBreweryId && (
          <Typography variant="body2" gutterBottom sx={{
            color: "text.secondary"
          }}>
            Parent Brewery:{' '}
            <Link
              to={`/brewery/${location.parentBreweryId}`}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              {location.parentBreweryId}
            </Link>
          </Typography>
        )}
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Added: {formatDate(location.timestamps?.createdDate)}
        </Typography>
      </CardContent>
    </Card>
  );
}

/**
 * UserAddedLocations page component displaying all user-added company locations
 */
function UserAddedLocations() {
  const pageTitle = 'User Added Locations';
  const { locations, loading, error } = useUserAddedLocations();

  return (
    <DashboardLayout title={pageTitle}>
      <Container fixed>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh"
            }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh"
            }}>
            <Typography variant="h6" color="error">
              Error loading locations: {error}
            </Typography>
          </Box>
        ) : (
          <Grid
            container
            spacing={0}
            direction="column"
            sx={{
              alignItems: "center",
              minHeight: '100vh',
              pt: '20px'
            }}>
            {locations.length === 0 ? (
              <Typography variant="h6" sx={{
                color: "text.secondary"
              }}>
                No user-added locations found
              </Typography>
            ) : (
              locations.map((location) => (
                <LocationCard key={location.id} location={location} />
              ))
            )}
          </Grid>
        )}
      </Container>
    </DashboardLayout>
  );
}

export default UserAddedLocations;
